export function unique(values) {
  const out = [];
  const seen = new Set();
  for (const value of values || []) {
    const x = String(value || '').trim();
    if (!x) continue;
    const key = x.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(x);
  }
  return out;
}

export function normaliseProjects(payload) {
  const state = payload?.state || payload || {};
  if (Array.isArray(payload?.projects)) return payload.projects;
  if (Array.isArray(state?.projects)) return state.projects;
  if (Array.isArray(payload)) return payload;
  return [];
}

// Null-safe on purpose: a default parameter only covers `undefined`, so
// getProjectId(null) used to throw "Cannot read property 'id' of null".
// ProjectContext's default value is `{ project: null }`, so any screen
// rendered outside ProjectProvider passes exactly that.
export function getProjectId(project) {
  if (!project) return '';
  return String(
    project.id ||
    project.projectId ||
    project.project_id ||
    project.projectNumber ||
    project.number ||
    project.name ||
    project.projectName ||
    'project'
  );
}

export function getProjectName(project) {
  if (!project) return '';
  return String(project.name || project.projectName || project.title || 'Untitled project');
}

// The id /api/state's lifecycle operations (archiveProject, deleteProject...)
// identify a project by. Must mirror stateProjectKey() in the API's state.js
// EXACTLY, including the fallback order and the lowercasing - a client that
// derives a different key deletes nothing, or worse, deletes the wrong row.
//
// The trailing `project-${index}` case only applies to a project carrying no
// id, number or name at all, so the index has to be the project's position in
// the same list the server stored.
export function getProjectKey(project = {}, index = 0) {
  return String(
    project.id
      || project.projectId
      || project.project_id
      || project.projectNumber
      || project.number
      || project.name
      || project.projectName
      || `project-${index}`,
  ).trim().toLowerCase();
}

function labelOf(value, keys = []) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  for (const key of keys) {
    const found = value?.[key];
    if (found != null && String(found).trim()) return String(found).trim();
  }
  return '';
}

const DEFAULT_TIER_DEFINITIONS = [
  { id: 'tier-1', name: 'Block', order: 0 },
  { id: 'tier-2', name: 'Level', order: 1 },
];

// Recursive root -> leaf walker over a project's master tree, any number of
// tiers deep.
export function flattenTree(master = [], tierDefinitions = DEFAULT_TIER_DEFINITIONS) {
  const paths = [];
  function walk(nodes, depth, ancestors) {
    for (const node of Array.isArray(nodes) ? nodes : []) {
      const tier = tierDefinitions[depth];
      const value = labelOf(node, ['name', 'title']);
      const entry = { tierId: tier?.id, tierName: tier?.name, nodeId: node?.id, value };
      const nextAncestors = [...ancestors, entry];
      const children = Array.isArray(node?.children) ? node.children : [];
      if (children.length) walk(children, depth + 1, nextAncestors);
      else paths.push({ leafId: node?.id, path: nextAncestors });
    }
  }
  walk(master, 0, []);
  return paths;
}

function cleanValue(value) {
  return String(value ?? '').trim();
}

// Unique values available at one tier depth, filtered by the shallower tiers
// already chosen. Shared by the mobile Upload Location picker and the web
// upload page so the cascade behaves identically in both.
//
// `selectedTiers` must be ANCESTORS ONLY (slice(0, depth)) - passing the full
// selection makes a tier filter against its own chosen value, which hides
// every sibling the moment one is picked.
export function tierOptionsAtDepth(tierPaths, depth, selectedTiers = []) {
  const seen = new Set();
  const out = [];
  for (const tp of Array.isArray(tierPaths) ? tierPaths : []) {
    const path = Array.isArray(tp?.path) ? tp.path : [];
    if (path.length <= depth) continue;
    const matchesAncestors = selectedTiers.every(
      (sel, i) => cleanValue(path[i]?.value).toLowerCase() === cleanValue(sel.value).toLowerCase(),
    );
    if (!matchesAncestors) continue;
    const value = cleanValue(path[depth]?.value);
    const key = value.toLowerCase();
    if (!value || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.sort((a, b) => {
    const an = a.match(/-?\d+/);
    const bn = b.match(/-?\d+/);
    if (an && bn) {
      const diff = Number(an[0]) - Number(bn[0]);
      if (diff !== 0) return diff;
    }
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  });
}

// The two axes an upload can be filed against: packages (who does the work)
// and the location tier tree (where it is). Prefers the server's already-built
// tierDefinitions/tierPaths from /api/mobile/upload-targets, falling back to
// deriving them from the project blob when called without an API response.
export function extractTargets(project = {}, apiTargets = null) {
  const source = apiTargets || project || {};

  const tierDefinitions = Array.isArray(source.tierDefinitions) && source.tierDefinitions.length
    ? source.tierDefinitions
    : DEFAULT_TIER_DEFINITIONS;

  return {
    packages: unique([
      ...(Array.isArray(source.packages) ? source.packages : []).map((p) => labelOf(p, ['name', 'packageName', 'package', 'title', 'label'])),
      ...(Array.isArray(source.responsibilities) ? source.responsibilities : []).map((p) => labelOf(p, ['name', 'packageName', 'package', 'supplier', 'title', 'label'])),
    ]),
    responsibilities: Array.isArray(source.responsibilities) ? source.responsibilities : [],
    tierDefinitions,
    tierPaths: Array.isArray(source.tierPaths) && source.tierPaths.length
      ? source.tierPaths
      : flattenTree(source.master || [], tierDefinitions),
  };
}
